import React, { ReactElement, useContext } from 'react';
import { FormTemplateType, useUpdateFormTemplateMutation } from 'shared/types/graphql';
import { FormsContext } from '../context/FormsContext';
import * as Sentry from '@sentry/react';
import { STATUSES, useCustomToast } from 'shared/hooks/useCustomToast';
import Collapsible from 'shared/components/CollapsibleFooter';
import { useTracking } from 'shared/providers/TrackingProvider';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { ServiceReminderEvents } from 'shared/providers/TrackingProvider/EventNames';
import useClinicUser from 'shared/hooks/useClinicUser';

interface IFloatingActionToolbar {
  getFormTemplates: () => void;
  templatesLoading: boolean;
}

const FloatingActionToolbar = ({ getFormTemplates, templatesLoading }: IFloatingActionToolbar): ReactElement => {
  const { formId, introMessage, questions, formName, formTemplateType, hasChanges, dispatch, autoExportEnabled } =
    useContext(FormsContext);

  const { primaryIntegration, primaryIntegrationName } = useIntegrationsProvider();

  const [updateFormTemplate, { loading: saveLoading }] = useUpdateFormTemplateMutation();
  const { toastMessagePop } = useCustomToast();
  const { currentClinicId, currentClinic } = useClinicUser();
  const { trackEvent } = useTracking();

  const onSaveTemplate = async (): Promise<void> => {
    try {
      if (formTemplateType === FormTemplateType.ServiceReminder) {
        trackEvent(ServiceReminderEvents.ServiceRemindersFormUpdated, {
          clinicId: currentClinicId,
          integrationType: primaryIntegration?.type,
          clinicName: currentClinic?.name,
          pims: primaryIntegrationName || '',
        });
      }
      await updateFormTemplate({
        variables: {
          where: {
            id: formId,
          },
          data: {
            title: formName,
            formTemplateType,
            autoExportEnabled,
            content: {
              formName,
              introMessage,
              questions,
            },
          },
        },
      });

      toastMessagePop('Form saved');

      getFormTemplates();

      dispatch({ type: 'RESET_HAS_CHANGES', payload: {} });
      dispatch({ type: 'RESET_NEW_FORM', payload: {} });
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      toastMessagePop(
        'Whoops! There was a problem saving this form. Please refresh the page and try again.',
        STATUSES.error,
      );
    }
  };

  const onCancel = (): void => {
    dispatch({ type: 'RESET_FORM', payload: {} });
  };

  return (
    <Collapsible
      onSave={onSaveTemplate}
      onCancel={onCancel}
      isLoading={saveLoading || templatesLoading}
      hasChanges={hasChanges}
    />
  );
};

export default FloatingActionToolbar;
